import CheckIcon from '@mui/icons-material/CheckRounded';
import { Button, Typography } from '@mui/material';

import { cn } from '@/shared/lib/cn';

interface FlowCompleteProps {
  doneCount: number;
  skippedCount: number;
  onClose: () => void;
}

/**
 * The end of the run.
 *
 * Reports what actually happened rather than congratulating unconditionally:
 * a director who skipped two of four items is not finished, and saying "all
 * done" to them would be a lie they discover on the next screen. The skipped
 * count is stated plainly and framed as still waiting, so closing the flow
 * never feels like the work disappeared.
 */
export const FlowComplete = ({ doneCount, skippedCount, onClose }: FlowCompleteProps) => {
  const clearedEverything = skippedCount === 0 && doneCount > 0;
  const didNothing = doneCount === 0;

  const title = clearedEverything
    ? "That's everything"
    : didNothing
      ? 'Nothing changed'
      : 'Done for now';

  const body = didNothing
    ? 'You skipped every item. They are still waiting whenever you are ready.'
    : clearedEverything
      ? `You cleared ${doneCount === 1 ? '1 item' : `${String(doneCount)} items`}. We'll email you when something new needs you.`
      : `You cleared ${doneCount === 1 ? '1 item' : `${String(doneCount)} items`} and skipped ${skippedCount === 1 ? '1' : String(skippedCount)}. The skipped ${skippedCount === 1 ? 'one is' : 'ones are'} still on your list.`;

  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center" role="status">
      <span
        aria-hidden
        className={cn(
          'flex size-16 items-center justify-center rounded-full ring-8 [&>svg]:text-[32px]',
          didNothing
            ? 'bg-surface-subtle text-content-muted ring-surface-subtle/60'
            : 'bg-success/12 text-success ring-success/8',
        )}
      >
        <CheckIcon fontSize="inherit" />
      </span>

      <div className="flex flex-col gap-1">
        <Typography variant="h2" component="p">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {body}
        </Typography>
      </div>

      <Button
        variant="contained"
        size="large"
        onClick={onClose}
        className="w-full rounded-full"
      >
        Back to my list
      </Button>
    </div>
  );
};
